import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import NotificationsDropdown from 'flarum/forum/components/NotificationsDropdown';
import NotificationList from 'flarum/forum/components/NotificationList';
import Button from 'flarum/common/components/Button';
import Model from 'flarum/common/Model';
import GroupApplicationNotification from './components/GroupApplicationNotification';
import GroupJoinedNotification from './components/GroupJoinedNotification';
import DiscussionCreatedNotification from './components/DiscussionCreatedNotification';
import DiscussionDeletedNotification from './components/DiscussionDeletedNotification';
import DiscussionStickedNotification from './components/DiscussionStickedNotification';
import PostStickedNotification from './components/PostStickedNotification';

app.initializers.add('tf/cns-notifications', () => {
  // 注册 social-groups 模型类型（用于通知的 subject）
  // 这样 Flarum 的 store 才能正确加载 relationships.subject
  if (!app.store.models['social-groups']) {
    class SocialGroup extends Model {
      apiEndpoint() {
        return '/social-groups';
      }
    }
    
    SocialGroup.prototype.id = Model.attribute('id');
    SocialGroup.prototype.name = Model.attribute('name');
    SocialGroup.prototype.slug = Model.attribute('slug');
    SocialGroup.prototype.description = Model.attribute('description');
    SocialGroup.prototype.coverImage = Model.attribute('cover_image');
    SocialGroup.prototype.privacy = Model.attribute('privacy');
    SocialGroup.prototype.isContainer = Model.attribute('is_container');
    SocialGroup.prototype.createdAt = Model.attribute('created_at', Model.transformDate);
    
    app.store.models['social-groups'] = SocialGroup;
  }
  
  // 注册通知组件
  app.notificationComponents.groupApplication = GroupApplicationNotification;
  app.notificationComponents.groupJoined = GroupJoinedNotification;
  app.notificationComponents.discussionCreated = DiscussionCreatedNotification;
  app.notificationComponents.discussionDeleted = DiscussionDeletedNotification;
  app.notificationComponents.discussionSticked = DiscussionStickedNotification;
  app.notificationComponents.discussionSticked  = PostStickedNotification;
  
  // 扩展 NotificationList 的 groupHeaderItems，支持显示群组名称
  extend(NotificationList.prototype, 'groupHeaderItems', function (items, group, badges) {
    // 如果没有 discussion，检查是否有 social-groups 类型的 subject
    if (!group.discussion && group.notifications && group.notifications.length > 0) {
      const firstNotification = group.notifications[0];
      const subject = firstNotification.subject();
      
      // 如果是 social-groups 类型，显示群组名称
      // 类型存储在 subject.data.type 中
      if (subject && subject.data && subject.data.type === 'social-groups') {
        const groupName = subject.name ? subject.name() : '群组';
        const groupId = subject.id ? subject.id() : null;
        
        // 移除原有的 neutral 项，添加新的群组 header
        if (items.has('neutral')) {
          items.remove('neutral');
        }
        
        items.add('group', 
          groupId ? (
            <a className="NotificationGroup-header" href={app.route('groupDetail', { id: groupId })}>
              <span>{groupName}</span>
            </a>
          ) : (
            <div className="NotificationGroup-header">
              <span>{groupName}</span>
            </div>
          ),
          0
        );
      }
    }
    
    return items;
  });
  
  try {
    // 获取基础路径的辅助函数
    const getBasePath = () => {
      let basePath = '';
      
      // 方法1: 从 Flarum 配置获取
      const baseUrl = app.forum.attribute('baseUrl') || '';
      if (baseUrl) {
        try {
          basePath = new URL(baseUrl).pathname;
        } catch (e) {
          // 如果 baseUrl 不是完整 URL，直接使用
          basePath = baseUrl.startsWith('/') ? baseUrl : '/' + baseUrl;
        }
      }
      
      // 方法2: 从 window.location 提取基础路径
      if (!basePath && typeof window !== 'undefined') {
        const fullPath = window.location.pathname;
        // 查找 /public 或基础路径
        const publicIndex = fullPath.indexOf('/public');
        if (publicIndex >= 0) {
          basePath = fullPath.substring(0, publicIndex + 7); // 包含 /public
        } else {
          // 如果没有 /public，尝试从路径中提取基础路径
          const pathParts = fullPath.split('/').filter(p => p);
          if (pathParts.length > 1) {
            basePath = '/' + pathParts.slice(0, -1).join('/');
          }
        }
      }
      
      return basePath;
    };
    
    // 规范化href路径（用于href属性，包含基础路径）
    const normalizeHrefPath = (path) => {
      if (!path || path === '#' || path === '') {
        path = '/';
      }
      
      // 如果是完整 URL（http:// 或 https://），直接返回
      if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
      }
      
      // 移除 # 前缀（如果存在）
      let routePath = path;
      if (routePath.startsWith('#')) {
        routePath = routePath.substring(1);
      }
      
      // 确保路径以 / 开头
      if (!routePath.startsWith('/')) {
        routePath = '/' + routePath;
      }
      
      // 获取基础路径并添加到路由路径（用于href属性）
      const basePath = getBasePath();
      if (basePath && basePath !== '/') {
        // 确保 basePath 不以 / 结尾
        const normalizedBasePath = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
        // 组合基础路径和路由路径
        return normalizedBasePath + routePath;
      }
      
      return routePath;
    };

    // ========== 修改通知按钮图标 ==========
    
    // 扩展 NotificationsDropdown 组件，修改图标
    extend(NotificationsDropdown.prototype, 'initAttrs', function (attrs) {
      // 修改默认图标为 fa-light fa-bell
      if (!attrs.icon) {
        attrs.icon = 'fa-light fa-bell';
      }
    });
    
    // 扩展 getButtonContent 方法，修改图标 HTML 结构
    extend(NotificationsDropdown.prototype, 'getButtonContent', function (content) {
      // content 参数是原始方法的返回值（数组）
      if (content && Array.isArray(content)) {
        // 查找图标元素并替换
        // 图标通常是数组的第一个元素，且包含 Button-icon 类
        for (let i = 0; i < content.length; i++) {
          const item = content[i];
          // 检查是否是图标元素（可能是 icon() helper 返回的 vnode 或直接的元素）
          if (item && (
            (item.attrs && item.attrs.className && item.attrs.className.includes('Button-icon')) ||
            (item.tag === 'i' && item.attrs && item.attrs.className && item.attrs.className.includes('icon'))
          )) {
            // 替换为自定义图标
            content[i] = <i className="icon fa-light fa-bell Button-icon"></i>;
            break;
          }
        }
      }
      
      return content;
    });

    // ========== 在通知列表中添加跳转按钮 ==========
    
    // 方法1: 扩展 NotificationsDropdown 的 getMenu 方法，在菜单内容中添加按钮
    extend(NotificationsDropdown.prototype, 'getMenu', function (menu) {
      // getMenu 返回的是 JSX 元素，包含 NotificationList
      // 我们需要在 NotificationList 后面添加按钮
      if (menu && menu.children && Array.isArray(menu.children)) {
        const buttonText = app.translator.trans('tf-cns-notifications.forum.notifications.view_all') || '查看全部通知';
        
        // 创建按钮元素
        const viewAllButton = m('a', {
          className: 'Button Button--primary CnsNotifications-view-all-button',
          href: normalizeHrefPath('/notifications'),
          onclick: (e) => {
            e.stopPropagation();
            // 关闭通知下拉菜单
            if (this.hide) {
              this.hide();
            }
          }
        }, buttonText);
        
        // 将按钮添加到菜单的 children 末尾
        menu.children.push(viewAllButton);
      }
      
      return menu;
    });
    
    // 方法2: 扩展 NotificationsDropdown 的 view 方法，在 NotificationList 容器最底部添加跳转按钮
    extend(NotificationsDropdown.prototype, 'view', function (vnode) {
      // 检查 m 函数是否可用
      if (typeof m === 'undefined') {
        return vnode;
      }
      
      // 递归查找并修改 NotificationList 元素
      const addButtonToNotificationList = (node, depth = 0) => {
        if (!node) return node;
        
        // 如果是数组，递归处理每个元素
        if (Array.isArray(node)) {
          return node.map(child => addButtonToNotificationList(child, depth + 1));
        }
        
        // 如果是对象且有 attrs 属性
        if (node.attrs && typeof node.attrs === 'object') {
          const className = node.attrs.className;
          
          // 检查是否是 NotificationList 元素（但不包括 NotificationList-content）
          const isNotificationList = className && (
            (typeof className === 'string' && className.includes('NotificationList') && !className.includes('NotificationList-content')) ||
            (typeof className === 'string' && /NotificationList\b/.test(className) && !/NotificationList-content/.test(className))
          );
          
          if (isNotificationList) {
            // 确保 children 是数组
            if (!node.children) {
              node.children = [];
            } else if (!Array.isArray(node.children)) {
              node.children = [node.children];
            }
            
            // 检查是否已经添加过按钮（避免重复添加）
            const hasButton = node.children.some(child => {
              if (child && child.attrs && child.attrs.className) {
                return child.attrs.className.includes('CnsNotifications-view-all-button');
              }
              return false;
            });
            
            if (!hasButton) {
              try {
                // 创建跳转按钮（使用普通 a 标签，因为这是完整 URL）
                const buttonText = app.translator.trans('tf-cns-notifications.forum.notifications.view_all') || '查看全部通知';
                
                let viewAllButton;
                if (typeof m !== 'undefined') {
                  viewAllButton = m('a', {
                    className: 'Button Button--primary CnsNotifications-view-all-button',
                    href: normalizeHrefPath('/notifications'),
                    onclick: (e) => {
                      e.stopPropagation();
                      // 关闭通知下拉菜单
                      if (this.hide) {
                        this.hide();
                      }
                      // 允许默认行为（跳转到 URL）
                    }
                  }, buttonText);
                } else {
                  // 如果 m 不可用，尝试使用 Button 组件
                  console.warn('[CNS-Notifications] m 函数不可用，尝试使用 Button 组件');
                  viewAllButton = Button.component({
                    className: 'Button--primary CnsNotifications-view-all-button',
                    onclick: () => {
                      // 关闭通知下拉菜单
                      if (this.hide) {
                        this.hide();
                      }
                      // 跳转到 URL
                      window.location.href = normalizeHrefPath('/notifications');
                    }
                  }, buttonText);
                }
                
                // 将按钮添加到 NotificationList 的 children 末尾（最底部）
                node.children.push(viewAllButton);
              } catch (error) {
                console.error('[CNS-Notifications] 创建按钮时出错:', error);
              }
            }
          }
        }
        
        // 递归处理 children
        if (node.children) {
          node.children = addButtonToNotificationList(node.children, depth + 1);
        }
        
        return node;
      };
      
      // 处理 vnode
      const result = addButtonToNotificationList(vnode);
      
      return result;
    });

    // ========== 实时通知轮询功能 ==========
    
    let notificationTimer = null;
    let pollCount = 0; // 轮询计数器
    let lastUnreadCount = null; // 上次未读通知数量
    const NOTIFICATION_POLL_INTERVAL = 5000; // 5秒轮询一次

    // 启动通知轮询
    const startNotificationPolling = () => {
      // 如果定时器已存在或用户未登录，则不启动
      if (notificationTimer || !app.session || !app.session.user) {
        return;
      }

      pollCount = 0;
      lastUnreadCount = null; // 重置未读数量记录
      
      // 立即执行一次轮询
      performNotificationPoll();
      
      notificationTimer = setInterval(() => {
        performNotificationPoll();
      }, NOTIFICATION_POLL_INTERVAL);
    };

    // 更新 NotificationsDropdown 显示
    const updateNotificationsDropdown = () => {
      try {
        // 触发 Mithril 重新渲染，更新 NotificationsDropdown
        if (typeof m !== 'undefined' && typeof m.redraw === 'function') {
          m.redraw();
        } else {
          // 如果 Mithril 不可用，尝试直接更新 DOM
          const unreadElement = document.querySelector('.NotificationsDropdown-unread');
          if (unreadElement && app.session && app.session.user) {
            const unreadCount = app.session.user.unreadNotificationCount();
            if (unreadCount > 0) {
              unreadElement.textContent = unreadCount;
              unreadElement.style.display = '';
            } else {
              unreadElement.style.display = 'none';
            }
          }
        }
      } catch (error) {
        console.error('[CNS-Notifications] 更新 NotificationsDropdown 失败:', error);
      }
    };

    // 检查是否为认证错误（登录态失效）
    const isAuthenticationError = (error) => {
      if (!error) return false;
      
      // 检查 HTTP 状态码
      if (error.status === 401 || error.status === 403) {
        return true;
      }
      
      // 检查错误响应中的状态码
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        return true;
      }
      
      // 检查错误消息中是否包含认证相关关键词
      const errorMessage = error.message || error.toString() || '';
      const authKeywords = ['unauthorized', 'unauthenticated', 'authentication', 'login', 'session', 'token', '401', '403'];
      if (authKeywords.some(keyword => errorMessage.toLowerCase().includes(keyword))) {
        return true;
      }
      
      // 检查错误对象中是否有认证相关的属性
      if (error.code === 'UNAUTHORIZED' || error.code === 'FORBIDDEN') {
        return true;
      }
      
      return false;
    };

    // 执行通知轮询
    const performNotificationPoll = () => {
      try {
        // 检查用户是否仍然登录（严格验证）
        if (!app.session || !app.session.user) {
          stopNotificationPolling();
          return;
        }

        // 验证用户 ID 是否存在且有效
        const userId = app.session.user.id();
        if (!userId || userId === null || userId === undefined) {
          console.warn(`[CNS-Notifications] ⚠️ [${new Date().toLocaleTimeString()}] 用户ID无效，停止轮询`);
          stopNotificationPolling();
          return;
        }

        pollCount++;

        // 获取当前未读通知数量
        const getCurrentUnreadCount = () => {
          try {
            if (app.session && app.session.user && typeof app.session.user.unreadNotificationCount === 'function') {
              return app.session.user.unreadNotificationCount();
            }
          } catch (error) {
            console.warn('[CNS-Notifications] 获取未读通知数量失败:', error);
          }
          return 0;
        };

        // 记录轮询前的未读数量
        const beforeUnreadCount = getCurrentUnreadCount();

        // 轮询获取最新通知和用户数据
        if (app.store && typeof app.store.find === 'function' && app.session && app.session.user) {
          // 再次验证登录态（在发起请求前）
          if (!app.session.user || !app.session.user.id()) {
            console.warn(`[CNS-Notifications] ⚠️ [${new Date().toLocaleTimeString()}] 请求前验证登录态失败，停止轮询`);
            stopNotificationPolling();
            return;
          }
          
          // 同时获取通知列表和用户数据
          Promise.all([
            // 获取通知列表（这会更新未读数量）
            app.store.find('notifications', { page: { limit: 10 } }),
            // 刷新当前用户数据（获取最新的未读通知数量）
            app.store.find('users', userId).catch(() => {
              // 如果失败，尝试使用 'me' 端点
              return app.store.find('users', 'me');
            })
          ])
            .then(([notificationsResult, user]) => {
              // 验证响应后用户是否仍然登录
              if (!app.session || !app.session.user) {
                console.warn(`[CNS-Notifications] ⚠️ [${new Date().toLocaleTimeString()}] 响应后用户登录态失效，停止轮询`);
                stopNotificationPolling();
                return;
              }

              // 通知列表和用户数据都已刷新，现在获取最新的未读数量
              setTimeout(() => {
                const afterUnreadCount = getCurrentUnreadCount();
                
                // 检查未读数量是否有变化
                if (lastUnreadCount !== null && afterUnreadCount !== lastUnreadCount) {
                  // 触发 NotificationsDropdown 更新
                  updateNotificationsDropdown();
                }
                
                // 更新上次未读数量
                lastUnreadCount = afterUnreadCount;
              }, 100); // 延迟100ms确保数据已更新
            })
            .catch(error => {
              // 检查是否为认证错误
              if (isAuthenticationError(error)) {
                console.warn(`[CNS-Notifications] ⚠️ [${new Date().toLocaleTimeString()}] 检测到登录态失效 (第 ${pollCount} 次)，停止轮询`);
                stopNotificationPolling();
                return;
              }

              console.error(`[CNS-Notifications] ❌ [${new Date().toLocaleTimeString()}] 用户数据刷新失败 (第 ${pollCount} 次):`, error);
              
              // 如果用户数据刷新失败，尝试只获取通知列表
              // 但在发起请求前再次验证登录态
              if (!app.session || !app.session.user) {
                console.warn(`[CNS-Notifications] ⚠️ [${new Date().toLocaleTimeString()}] 重试前登录态已失效，停止轮询`);
                stopNotificationPolling();
                return;
              }

              return app.store.find('notifications', { page: { limit: 1 } })
                .then(() => {
                  // 验证响应后用户是否仍然登录
                  if (!app.session || !app.session.user) {
                    console.warn(`[CNS-Notifications] ⚠️ [${new Date().toLocaleTimeString()}] 通知列表响应后登录态失效，停止轮询`);
                    stopNotificationPolling();
                    return;
                  }

                  const afterUnreadCount = getCurrentUnreadCount();
                  
                  if (lastUnreadCount !== null && afterUnreadCount !== lastUnreadCount) {
                    updateNotificationsDropdown();
                  }
                  
                  lastUnreadCount = afterUnreadCount;
                })
                .catch(err => {
                  // 检查是否为认证错误
                  if (isAuthenticationError(err)) {
                    console.warn(`[CNS-Notifications] ⚠️ [${new Date().toLocaleTimeString()}] 通知列表获取时检测到登录态失效，停止轮询`);
                    stopNotificationPolling();
                    return;
                  }

                  console.error(`[CNS-Notifications] ❌ 通知列表获取也失败:`, err);
                });
            });
        } else {
          // 如果 store.find 不可用，跳过本次轮询
          if (!app.store) {
            console.warn(`[CNS-Notifications] ⚠️ [${new Date().toLocaleTimeString()}] app.store 未准备好，跳过轮询`);
          } else if (!app.session || !app.session.user) {
            console.warn(`[CNS-Notifications] ⚠️ [${new Date().toLocaleTimeString()}] 用户未登录，停止轮询`);
            stopNotificationPolling();
          }
        }
      } catch (error) {
        console.error(`[CNS-Notifications] ❌ [${new Date().toLocaleTimeString()}] 通知轮询异常 (第 ${pollCount} 次):`, error);
        
        // 如果捕获到认证错误，停止轮询
        if (isAuthenticationError(error)) {
          console.warn(`[CNS-Notifications] ⚠️ [${new Date().toLocaleTimeString()}] 轮询异常中检测到登录态失效，停止轮询`);
          stopNotificationPolling();
        }
      }
    };

    // 停止通知轮询
    const stopNotificationPolling = () => {
      if (notificationTimer) {
        clearInterval(notificationTimer);
        notificationTimer = null;
        pollCount = 0;
        lastUnreadCount = null; // 重置未读数量记录
      }
    };

    // 延迟初始化，确保 app.session 已准备好
    const initNotificationPolling = () => {
      try {
        // 初始化：如果用户已登录，启动轮询
        if (app.session && app.session.user) {
          startNotificationPolling();
        }

        // 监听用户登录/登出事件
        if (app.session && typeof app.session.on === 'function') {
          app.session.on('change:user', (user) => {
            if (user) {
              // 用户登录，启动轮询
              startNotificationPolling();
            } else {
              // 用户登出，停止轮询
              stopNotificationPolling();
            }
          });
        }

        // 监听页面可见性变化
        if (typeof document !== 'undefined') {
          document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
              // 页面隐藏，停止轮询以节省资源
              stopNotificationPolling();
            } else {
              // 页面显示，重新启动轮询
              if (app.session && app.session.user) {
                startNotificationPolling();
              }
            }
          });
        }
      } catch (error) {
        console.error('[CNS-Notifications] 初始化通知轮询失败:', error);
      }
    };

    // 延迟初始化，确保所有依赖都已加载
    if (typeof m !== 'undefined' && m.redraw) {
      // 使用 Mithril 的 next tick 确保在渲染后初始化
      setTimeout(initNotificationPolling, 100);
    } else {
      // 如果 Mithril 未准备好，直接初始化
      initNotificationPolling();
    }
  } catch (error) {
    console.error('[CNS-Notifications] 插件初始化失败:', error);
    // 重新抛出错误以便 Flarum 能够显示错误信息
    throw error;
  }
});